import { act, fireEvent, render, screen, waitFor } from '@testing-library/react';
import { Link, MemoryRouter, Route, Routes } from 'react-router-dom';
import Reporting from './index';
import { askReporting, getReporting } from '../../api/reporting';

jest.mock('../../api/reporting', () => ({ getReporting: jest.fn(), askReporting: jest.fn() }));

const report = {
  section: 'overview',
  metrics: { people: 1, attendancePercent: 75, presentDays: 15, absentDays: 5, leaveDays: 0 },
  departments: [{ id: 'team', name: 'Team' }],
  people: [{ id: 'ada', name: 'Ada User', departmentId: 'team', departmentIds: ['team'] }],
  employees: [{ id: 'ada', name: 'Ada User', department: 'Team', attendancePercent: 75 }],
  definitions: { attendancePercent: 'Present days divided by expected workdays.' },
};

beforeEach(() => {
  jest.clearAllMocks();
  getReporting.mockResolvedValue(report);
});

it('shows a scoped report and refetches when a section changes', async () => {
  render(<MemoryRouter initialEntries={['/reporting']}><Reporting /></MemoryRouter>);
  expect((await screen.findAllByText('Ada User')).length).toBeGreaterThan(0);
  expect(screen.getAllByText('75%').length).toBeGreaterThan(0);
  fireEvent.change(screen.getByLabelText('Section'), { target: { value: 'attendance' } });
  await waitFor(() => expect(getReporting).toHaveBeenLastCalledWith(expect.objectContaining({ section: 'attendance' })));
  await waitFor(() => expect(screen.queryByRole('status')).not.toBeInTheDocument());
});

it('shows the global loading state while a different section loads', async () => {
  let finishRefresh;
  getReporting.mockResolvedValueOnce(report).mockImplementationOnce(() => new Promise((resolve) => { finishRefresh = resolve; }));
  render(<MemoryRouter initialEntries={['/reporting']}><Reporting /></MemoryRouter>);
  expect(await screen.findByText('Present days divided by expected workdays.')).toBeInTheDocument();
  fireEvent.change(screen.getByLabelText('Section'), { target: { value: 'attendance' } });
  expect(screen.getByRole('status')).toHaveTextContent('Loading reports...');
  expect(screen.queryByText('Present days divided by expected workdays.')).not.toBeInTheDocument();
  expect(screen.queryByText('75%')).not.toBeInTheDocument();
  expect(screen.getByRole('button', { name: 'Export CSV' })).toBeDisabled();
  await act(async () => { finishRefresh({ ...report, metrics: { people: 1, attendancePercent: 80 } }); });
  expect(screen.getAllByText('80%').length).toBeGreaterThan(0);
  expect(screen.queryByRole('status')).not.toBeInTheDocument();
});

it('allows a failed report request to be retried', async () => {
  getReporting.mockRejectedValueOnce({ response: { data: { message: 'Temporary reporting failure' } } }).mockResolvedValueOnce(report);
  render(<MemoryRouter initialEntries={['/reporting']}><Reporting /></MemoryRouter>);
  expect(await screen.findByRole('alert')).toHaveTextContent('Temporary reporting failure');
  fireEvent.click(screen.getByRole('button', { name: 'Retry' }));
  expect(await screen.findByText('Present days divided by expected workdays.')).toBeInTheDocument();
  expect(getReporting).toHaveBeenCalledTimes(2);
});

it('preserves the report when switching to Intelligence and back', async () => {
  render(<MemoryRouter initialEntries={['/reporting']}><Link to="/reporting/intelligence">Intelligence sidebar link</Link><Link to="/reporting">Reports sidebar link</Link><Routes><Route path="/reporting/*" element={<Reporting />} /></Routes></MemoryRouter>);
  expect(await screen.findByText('Present days divided by expected workdays.')).toBeInTheDocument();
  expect(screen.queryByRole('navigation', { name: 'Reporting tabs' })).not.toBeInTheDocument();
  fireEvent.click(screen.getByRole('link', { name: 'Intelligence sidebar link' }));
  expect(screen.getByText('Ask about your organization')).toBeInTheDocument();
  fireEvent.click(screen.getByRole('link', { name: 'Reports sidebar link' }));
  expect(screen.getAllByText('75%').length).toBeGreaterThan(0);
  expect(getReporting).toHaveBeenCalledTimes(1);
});

it('switches between report and chart views without fetching again', async () => {
  render(<MemoryRouter initialEntries={['/reporting']}><Reporting /></MemoryRouter>);
  expect(await screen.findByText('Present days divided by expected workdays.')).toBeInTheDocument();
  fireEvent.click(screen.getByRole('button', { name: 'Chart View' }));
  expect(screen.getByLabelText('Report charts')).toBeInTheDocument();
  expect(screen.getByText('Attendance days')).toBeInTheDocument();
  expect(screen.getByText('Employee comparison')).toBeInTheDocument();
  expect(screen.queryByRole('table')).not.toBeInTheDocument();
  fireEvent.click(screen.getByRole('button', { name: 'Report View' }));
  expect(screen.getByRole('table')).toBeInTheDocument();
  expect(getReporting).toHaveBeenCalledTimes(1);
});

it('shows the actual period returned by Intelligence', async () => {
  askReporting.mockResolvedValue({ answer: 'Ada User earned the most points.', range: { from: '2025-01-01', to: '2025-01-31' } });
  render(<MemoryRouter initialEntries={['/reporting/intelligence']}><Reporting /></MemoryRouter>);
  fireEvent.change(screen.getByLabelText('Your question'), { target: { value: 'Who earned the most points last month?' } });
  fireEvent.click(screen.getByRole('button', { name: 'Ask' }));
  expect(await screen.findByText('Period: 2025-01-01 to 2025-01-31')).toBeInTheDocument();
  expect(askReporting).toHaveBeenCalledWith('Who earned the most points last month?', expect.objectContaining({ from: expect.any(String), to: expect.any(String) }));
  expect(getReporting).not.toHaveBeenCalled();
});

it('opens recommendations as a separate reporting subtab', async () => {
  render(<MemoryRouter initialEntries={['/reporting/recommendations']}><Reporting /></MemoryRouter>);
  expect(await screen.findByRole('region', { name: 'Recommendations' })).toBeInTheDocument();
  expect(await screen.findByText('Not enough activity to recommend a change')).toBeInTheDocument();
  expect(screen.getByText('Try a recommendation question')).toBeInTheDocument();
  expect(getReporting).toHaveBeenCalledWith(expect.objectContaining({ section: 'overview' }));
  expect(screen.queryByLabelText('Report filters')).not.toBeInTheDocument();
});

it('asks a suggested question immediately when clicked', async () => {
  askReporting.mockResolvedValue({ answer: 'There are 4 departments.' });
  render(<MemoryRouter initialEntries={['/reporting/intelligence']}><Reporting /></MemoryRouter>);
  fireEvent.click(screen.getByRole('button', { name: 'How many departments do we have?' }));
  expect(askReporting).toHaveBeenCalledWith('How many departments do we have?', expect.objectContaining({ from: expect.any(String), to: expect.any(String) }));
  expect(screen.getByLabelText('Your question')).toHaveValue('How many departments do we have?');
  expect(await screen.findByText('There are 4 departments.')).toBeInTheDocument();
});

it('does not show a stale Intelligence answer after the date range changes', async () => {
  let resolveAnswer;
  askReporting.mockImplementation(() => new Promise((resolve) => { resolveAnswer = resolve; }));
  render(<MemoryRouter initialEntries={['/reporting/intelligence']}><Reporting /></MemoryRouter>);
  fireEvent.change(screen.getByLabelText('Your question'), { target: { value: 'Who earned the most points?' } });
  fireEvent.click(screen.getByRole('button', { name: 'Ask' }));
  fireEvent.change(screen.getByLabelText('From'), { target: { value: '2025-01-01' } });
  await act(async () => { resolveAnswer({ answer: 'Old answer', range: { from: '2025-01-01', to: '2025-01-31' } }); });
  expect(screen.queryByText('Old answer')).not.toBeInTheDocument();
  expect(screen.getByRole('button', { name: 'Ask' })).toBeEnabled();
});
